import React from "react";
import StatCard from "../../StatCard";
import { RevenueIcon, CashIcon, DealsIcon } from "../../ui/Icons";
import { formatUSD } from "@/utils/formatters";

// =============================================================================
// CLIENT STATS
// =============================================================================
// PURPOSE: Dashboard section for client role — limited KPI view.
//
// VISIBILITY: Only shown to client (see roleConfig.ts).
//
// DISPLAYED METRICS:
// - Financial: Total revenue, cash collected, deals closed, rev/cash ratio
// - Performance: Show rate, close rate
// - Volume: Calls booked, calls taken, AOV, cash per call (taken/booked)
//
// DESIGN DECISION: Clients see their own revenue performance but not team-level
// data or confidential breakdowns. Keeps focus on outcomes, not operations.
// =============================================================================

interface ClientStatsProps {
  kpis: {
    revenueClosed: number;
    cashCollected: number;
    dealsClosed: number;
    revenueToCashRatio: string;
    showUpRate: number;
    closeRate: number;
    callsBooked: number;
    callsTaken: number;
    aov: number;
    cashPerCallTaken: number;
    cashPerCallBooked: number;
    revenueChange?: number;
    cashChange?: number;
    dealsChange?: number;
    aovChange?: number;
    closeRateChange?: number;
    showUpRateChange?: number;
    cashPerCallTakenChange?: number;
    cashPerCallBookedChange?: number;
  };
  getSamples: (key: string) => any[];
}

const ClientStats: React.FC<ClientStatsProps> = ({ kpis, getSamples }) => (
  <div className="space-y-3 sm:space-y-6">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
      <StatCard
        title="Total Revenue"
        value={formatUSD(kpis.revenueClosed)}
        icon={<RevenueIcon />}
        percentChange={kpis.revenueChange}
        metricKey="revenueClosed"
        getSamples={getSamples}
      />
      <StatCard
        title="Cash Collected"
        value={formatUSD(kpis.cashCollected)}
        icon={<CashIcon />}
        percentChange={kpis.cashChange}
        metricKey="cashCollected"
        getSamples={getSamples}
      />
      <StatCard
        title="Deals Closed"
        value={kpis.dealsClosed.toString()}
        icon={<DealsIcon />}
        percentChange={kpis.dealsChange}
        changeFormat="number"
        metricKey="dealsClosed"
        getSamples={getSamples}
      />
      <StatCard
        title="Rev / Cash"
        value={kpis.revenueToCashRatio}
        subtext={kpis.revenueToCashRatio === "1.00:1" ? "Fully Collected" : "Revenue to Cash"}
        metricKey="revenueToCashRatio"
        getSamples={getSamples}
      />
      <StatCard
        title="Show Rate"
        value={`${kpis.showUpRate.toFixed(1)}%`}
        percentChange={kpis.showUpRateChange}
        metricKey="showUpRate"
        getSamples={getSamples}
      />
      <StatCard
        title="Close Rate"
        value={`${kpis.closeRate.toFixed(1)}%`}
        percentChange={kpis.closeRateChange}
        metricKey="closeRate"
        getSamples={getSamples}
      />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3 sm:gap-4">
      <StatCard
        title="Calls Booked"
        value={kpis.callsBooked.toString()}
        metricKey="callsBooked"
        getSamples={getSamples}
      />
      <StatCard
        title="Calls Taken"
        value={kpis.callsTaken.toString()}
        metricKey="callsTaken"
        getSamples={getSamples}
      />
      <StatCard
        title="AOV"
        value={formatUSD(kpis.aov)}
        subtext="New Deals Only"
        percentChange={kpis.aovChange}
        changeFormat="dollar"
        metricKey="aov"
        getSamples={getSamples}
      />
      <StatCard
        title="Cash / Call Taken"
        value={formatUSD(kpis.cashPerCallTaken)}
        percentChange={kpis.cashPerCallTakenChange}
        changeFormat="dollar"
        metricKey="cashPerCallTaken"
        getSamples={getSamples}
      />
      <StatCard
        title="Cash / Call Booked"
        value={formatUSD(kpis.cashPerCallBooked)}
        percentChange={kpis.cashPerCallBookedChange}
        changeFormat="dollar"
        metricKey="cashPerCallBooked"
        getSamples={getSamples}
      />
    </div>
  </div>
);

export default ClientStats;
